import React from 'react';
import classNames from 'classnames';

import { Button } from '@eventbrite/valkyrie-eds-button';
import { SIZE_BLOCK, STYLE_LINK } from '@eventbrite/valkyrie-eds-button';
import { Divider } from '@eventbrite/valkyrie-eds-divider';
import { IconButton } from '@eventbrite/valkyrie-eds-icon-button';
import { NotificationBar } from '@eventbrite/valkyrie-eds-notification';

import { MODAL_BODY_PROP_TYPES, MODAL_TITLE_ID } from './constants';

import styles from './Modal.module.scss';

const _getNotificationClasses = (notificationOptions) => {
  const {
    isClosing,
    shouldFloatAboveContent,
    shouldAnimate,
    //Modal default behavior has been
    //to display notifications full width
    //at all breakpoints. This continues
    //that pattern but allows for user
    //to configure otherwise. Particularly
    //when using a ComplexModal on full
    //screen experiences.
    fullWidth = true,
    ...prunedOptions
  } = notificationOptions;

  const classes = classNames(styles.notification, {
    [styles.notificationIsClosing]: isClosing,
    [styles.notificationAbs]: shouldFloatAboveContent,
    [styles.notificationAnimate]: shouldAnimate,
    [styles.notificationFullWidth]: fullWidth,
  });

  return { classes, prunedOptions };
};

const _getNotifications = (notificationOptions) => {
  let notifications = null;

  if (notificationOptions) {
    const notificationOptionsList = Array.isArray(notificationOptions)
      ? notificationOptions
      : [notificationOptions];

    notifications = notificationOptionsList.map((notificationOption) => {
      const { classes, prunedOptions } =
        _getNotificationClasses(notificationOption);
      let key = notificationOption.children;

      if (notificationOption.children && notificationOption.children.key) {
        key = notificationOption.children.key;
      }

      return (
        <span className={classes} key={key}>
          <NotificationBar {...prunedOptions} />
        </span>
      );
    });
  }

  return notifications;
};

const Header = ({
  headerActionIconType,
  headerActionTitle,
  onHeaderAction,
  title,
  secondaryTitle,
}) => {
  let component = null;

  if (title) {
    component = (
      <header className={styles.header}>
        <HeaderAction
          headerActionIconType={headerActionIconType}
          headerActionTitle={headerActionTitle}
          onHeaderAction={onHeaderAction}
        />
        <TitleHeader title={title} secondaryTitle={secondaryTitle} />
      </header>
    );
  }

  return component;
};

const HeaderAction = ({
  headerActionIconType,
  headerActionTitle,
  onHeaderAction,
}) => {
  let component = null;

  if (headerActionIconType) {
    component = (
      <div className={styles.secondaryHeaderAction}>
        <IconButton
          data-automation="modal-header-action-button"
          data-spec="secondary-header-action"
          onClick={onHeaderAction}
          iconType={headerActionIconType}
          title={headerActionTitle}
        />
      </div>
    );
  }

  return component;
};

const TitleHeader = ({ title, secondaryTitle }) =>
  title ? (
    <div className={classNames(styles.titleContainer, styles.titleSpacing)}>
      <h2
        id={MODAL_TITLE_ID}
        className={styles.title}
        data-spec="eds-modal__title"
      >
        {title}
      </h2>
      {secondaryTitle && (
        <span
          className={styles.secondaryTitle}
          data-spec="eds-modal__secondary-title"
        >
          {secondaryTitle}
        </span>
      )}
      <div className={styles.titleSpacing}>
        <Divider />
      </div>
    </div>
  ) : null;

const Illustration = ({ illustration }) => {
  let component = null;

  if (illustration) {
    component = <div className={styles.illustration}>{illustration}</div>;
  }

  return component;
};

const PrimaryButton = ({
  onPrimaryAction,
  primaryText,
  primaryType,
  primarySize,
  primaryAttributes,
  primaryIsLoading,
  primaryIsDisabled,
  primaryKeepDisabledStyleWhileLoading,
}) => {
  let component = null;
  const automationSelector = styles.primaryButton;

  if (primaryText) {
    component = (
      <Button
        {...primaryAttributes}
        onClick={onPrimaryAction}
        style={primaryType}
        disabled={primaryIsDisabled}
        keepDisabledStyleWhileLoading={primaryKeepDisabledStyleWhileLoading}
        data-spec={automationSelector}
        data-automation={automationSelector}
        isLoading={primaryIsLoading}
        size={primarySize}
        __containerClassName={styles.buttonBarButton}
      >
        {primaryText}
      </Button>
    );
  }

  return component;
};

const SecondaryButton = ({
  onSecondaryAction,
  secondaryText,
  secondaryAttributes,
  secondaryType,
  secondaryIsDisabled,
}) => {
  let component = null;

  if (secondaryText) {
    component = (
      <div className={styles.secondaryButton}>
        <Button
          {...secondaryAttributes}
          onClick={onSecondaryAction}
          style={secondaryType}
          disabled={secondaryIsDisabled}
          data-spec="eds-modal__secondary-button"
          __containerClassName={styles.buttonBarButton}
        >
          {secondaryText}
        </Button>
      </div>
    );
  }

  return component;
};

const TertiaryButton = ({ onTertiaryAction, tertiaryNode }) => {
  let component = null;

  if (tertiaryNode) {
    component = onTertiaryAction ? (
      <Button
        onClick={onTertiaryAction}
        style="none"
        __containerClassName={styles.buttonBarTertiary}
      >
        {tertiaryNode}
      </Button>
    ) : (
      <div className={styles.buttonBarTertiary}>{tertiaryNode}</div>
    );
  }

  return component;
};

/**
 * When there is a message to display in the button bar. returns the requisite markup.
 * When there is no message, returns null.
 *
 * @param {String} message - If null or undefined, the component is null
 * @param {Boolean} hasBlockButton
 */
const ButtonBarMessage = ({ message, hasBlockButton }) => {
  let component = null;
  let messageClasses;

  if (message) {
    messageClasses = classNames(styles.message, {
      [styles.messageRight]: !hasBlockButton,
    });

    component = <div className={messageClasses}>{message}</div>;
  }

  return component;
};

const ButtonBar = ({
  primaryType,
  primaryText,
  primaryAttributes,
  primaryIsDisabled,
  primaryKeepDisabledStyleWhileLoading,
  primaryIsLoading,
  primarySize,
  onPrimaryAction,
  secondaryType,
  secondaryText,
  secondaryAttributes,
  secondaryIsDisabled,
  onSecondaryAction,
  title,
  tertiaryNode,
  onTertiaryAction,
  buttonBarMessage,
  shouldShowFooter,
  __footerContainerClassName,
}) => {
  const hasBlockButton =
    primarySize === SIZE_BLOCK && !secondaryText && !tertiaryNode;

  const footerClasses = classNames(
    {
      [styles.footerBlock]: hasBlockButton,
    },
    __footerContainerClassName
  );

  const buttonBarClasses = classNames(styles.buttonBar, {
    [styles.buttonBarWithTitle]: title,
    [styles.buttonBarWithTertiary]: tertiaryNode,
  });

  const primarySecondaryClasses = classNames(styles.buttonBarPrimarySecondary, {
    [styles.buttonBarBlock]: hasBlockButton,
  });

  if (!(primaryText || shouldShowFooter)) {
    return null;
  }

  return (
    <ModalFooter __footerContainerClassName={footerClasses}>
      <nav className={buttonBarClasses} role="none">
        <div className={primarySecondaryClasses}>
          <SecondaryButton
            onSecondaryAction={onSecondaryAction}
            secondaryText={secondaryText}
            secondaryType={secondaryType}
            secondaryAttributes={secondaryAttributes}
            secondaryIsDisabled={secondaryIsDisabled}
          />

          <PrimaryButton
            onPrimaryAction={onPrimaryAction}
            primaryText={primaryText}
            primaryType={primaryType}
            primarySize={primarySize}
            primaryAttributes={primaryAttributes}
            primaryIsDisabled={primaryIsDisabled}
            primaryKeepDisabledStyleWhileLoading={
              primaryKeepDisabledStyleWhileLoading
            }
            primaryIsLoading={primaryIsLoading}
          />
        </div>
        <TertiaryButton
          onTertiaryAction={onTertiaryAction}
          tertiaryNode={tertiaryNode}
        />
        <ButtonBarMessage
          hasBlockButton={hasBlockButton}
          message={buttonBarMessage}
        />
      </nav>
    </ModalFooter>
  );
};

export const ModalContent = ({
  children,
  title,
  secondaryTitle,
  illustration,
  headerActionIconType,
  headerActionTitle,
  onHeaderAction,
  notificationOptions,
}) => {
  const notifications = _getNotifications(notificationOptions);
  const mainClasses = classNames(styles.main, {
    [styles.mainRow]: illustration,
  });

  return (
    <div className={styles.container}>
      <Header
        headerActionIconType={headerActionIconType}
        headerActionTitle={headerActionTitle}
        onHeaderAction={onHeaderAction}
        title={title}
        secondaryTitle={secondaryTitle}
      />
      <main className={mainClasses} data-spec="eds-modal__main">
        <Illustration illustration={illustration} />
        <div className={styles.content}>
          {notifications}
          {children}
        </div>
      </main>
    </div>
  );
};

export const ModalContentChildren = ({ children }) => {
  return (
    <div id="edsModalContentChildren" className={styles.contentChildren}>
      {children}
    </div>
  );
};

export const ModalFooter = ({ children, __footerContainerClassName }) => {
  const footerClasses = classNames(styles.footer, __footerContainerClassName);

  return (
    <div className={styles.footerBackground}>
      <div className={footerClasses} data-spec="eds-modal__footer">
        {children}
      </div>
    </div>
  );
};

export class ModalBody extends React.PureComponent {
  static propTypes = MODAL_BODY_PROP_TYPES;

  static defaultProps = {
    primaryType: STYLE_LINK,
    secondaryType: STYLE_LINK,
  };

  render() {
    const {
      children,
      title,
      secondaryTitle,
      illustration,
      headerActionIconType,
      headerActionTitle,
      onHeaderAction,
      primaryType,
      primaryText,
      primaryAttributes,
      primarySize,
      primaryIsDisabled,
      primaryKeepDisabledStyleWhileLoading,
      primaryIsLoading,
      onPrimaryAction,
      secondaryType,
      secondaryText,
      secondaryAttributes,
      secondaryIsDisabled,
      onSecondaryAction,
      tertiaryNode,
      notificationOptions,
      onTertiaryAction,
      buttonBarMessage,
      shouldShowFooter,
      __footerContainerClassName,
    } = this.props;

    return (
      <ModalContent
        title={title}
        secondaryTitle={secondaryTitle}
        illustration={illustration}
        headerActionIconType={headerActionIconType}
        headerActionTitle={headerActionTitle}
        onHeaderAction={onHeaderAction}
        notificationOptions={notificationOptions}
      >
        <ModalContentChildren>{children}</ModalContentChildren>

        <ButtonBar
          primaryType={primaryType}
          primaryText={primaryText}
          primaryAttributes={primaryAttributes}
          primarySize={primarySize}
          primaryIsDisabled={primaryIsDisabled}
          primaryKeepDisabledStyleWhileLoading={
            primaryKeepDisabledStyleWhileLoading
          }
          onPrimaryAction={onPrimaryAction}
          secondaryType={secondaryType}
          secondaryText={secondaryText}
          secondaryAttributes={secondaryAttributes}
          secondaryIsDisabled={secondaryIsDisabled}
          onSecondaryAction={onSecondaryAction}
          title={title}
          primaryIsLoading={primaryIsLoading}
          tertiaryNode={tertiaryNode}
          onTertiaryAction={onTertiaryAction}
          buttonBarMessage={buttonBarMessage}
          shouldShowFooter={shouldShowFooter}
          __footerContainerClassName={__footerContainerClassName}
        />
      </ModalContent>
    );
  }
}
